import { isAnimalSuspicious, getAnimalHealthScore } from "../../utils/animalUtils";
import {getProfileId} from '../../domain/cattle/profile';

function safeDate(value) {
        if (!value) {
                return null;
        }

        if (value instanceof Date) {
                return value;
        }

        if (typeof value === "object" && typeof value.toDate === "function") {
                try {
                        return value.toDate();
                } catch (error) {
                        return null;
                }
        }

        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function analyzeAnimals(animals = []) {
        const total = animals.length;
        const groups = new Map();
        const locations = new Map();
        let cumulativeScore = 0;
        let scoreCount = 0;

        const suspicious = [];
        const lifecycleProfiles = {};

        animals.forEach((animal) => {
                const groupName = animal?.Group || "Unassigned";
                const location = animal?.Location || "-";
                groups.set(groupName, (groups.get(groupName) || 0) + 1);
                locations.set(location, (locations.get(location) || 0) + 1);

                const score = getAnimalHealthScore(animal);
                if (animal?.SchemaVersion === 2) {
                        const profileId = getProfileId(animal) || 'unclassified';
                        lifecycleProfiles[profileId] = (lifecycleProfiles[profileId] || 0) + 1;
                }
                if (score !== null) {
                        cumulativeScore += score;
                        scoreCount += 1;
                }

                const isSuspicious = isAnimalSuspicious(animal);
                if (isSuspicious) {
                        suspicious.push({
                                id: animal?.ID || animal?.id || "unknown",
                                mac: animal?.MAC || null,
                                healthScore: typeof animal?.HealthScore === "number" ? animal.HealthScore : null,
                                group: groupName,
                                location,
                                lastActivity: safeDate(animal?.LastActivity || animal?.LastSeen),
                        });
                }
        });

        suspicious.sort((a, b) => {
                const aScore = typeof a.healthScore === "number" ? a.healthScore : 101;
                const bScore = typeof b.healthScore === "number" ? b.healthScore : 101;
                return aScore - bScore;
        });

        const averageHealthScore = scoreCount > 0 ? Math.round((cumulativeScore / scoreCount) * 10) / 10 : null;

        const topGroups = Array.from(groups.entries())
                .sort(([, countA], [, countB]) => countB - countA)
                .slice(0, 5)
                .map(([name, count]) => ({ name, count }));

        return {
                total,
                lifecycleProfiles,
                averageHealthScore,
                suspicious,
                suspiciousCount: suspicious.length,
                groups: topGroups,
                groupSpread: groups,
                locations,
        };
}

export function analyzeTreatments(treatments = []) {
        const active = [];
        const completed = [];
        const archived = [];
        const dueToday = [];
        const overdue = [];
        const upcoming = [];

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        treatments.forEach((treatment) => {
                const isCompleted = Boolean(treatment?.Completed);
                const isArchived = Boolean(treatment?.Archived);
                const target = isCompleted ? completed : isArchived ? archived : active;
                target.push(treatment);

                if (!isCompleted && !isArchived) {
                        const scheduled = safeDate(treatment?.Date || treatment?.PlannedDate);
                        if (scheduled) {
                                const scheduledDay = new Date(scheduled.getFullYear(), scheduled.getMonth(), scheduled.getDate());
                                if (scheduledDay.getTime() === today.getTime()) {
                                        dueToday.push(treatment);
                                } else if (scheduledDay < today) {
                                        overdue.push(treatment);
                                } else if (scheduledDay.getTime() - today.getTime() <= 1000 * 60 * 60 * 24 * 3) {
                                        upcoming.push(treatment);
                                }
                        }
                }
        });

        return {
                total: treatments.length,
                active,
                completed,
                archived,
                dueToday,
                overdue,
                upcoming,
        };
}

export function analyzePredictions(predictions = []) {
        const buckets = {
                disease: [],
                birth: [],
                health: [],
                other: [],
        };

        predictions.forEach((prediction) => {
                const type = (prediction?.Type || prediction?.type || "").toLowerCase();
                if (type.includes("disease") || type.includes("brd") || type.includes("scours")) {
                        buckets.disease.push(prediction);
                } else if (type.includes("birth") || type.includes("calving")) {
                        buckets.birth.push(prediction);
                } else if (type.includes("health") || type.includes("fever") || type.includes("lameness")) {
                        buckets.health.push(prediction);
                } else {
                        buckets.other.push(prediction);
                }
        });

        return {
                total: predictions.length,
                disease: buckets.disease,
                birth: buckets.birth,
                health: buckets.health,
                other: buckets.other,
        };
}

export function buildOperationalAlerts({ animalStats, treatmentStats, predictionStats }) {
        const alerts = [];

        if (animalStats.suspiciousCount > 0) {
                alerts.push({
                        type: "animals",
                        severity: animalStats.suspiciousCount > 5 ? "high" : "medium",
                        message: `${animalStats.suspiciousCount} animals require attention`,
                        relatedAnimals: animalStats.suspicious.slice(0, 10),
                });
        }

        if (treatmentStats.overdue.length > 0) {
                        alerts.push({
                                type: "treatments",
                                severity: "high",
                                message: `${treatmentStats.overdue.length} treatments are overdue`,
                                relatedTreatments: treatmentStats.overdue.slice(0, 10),
                        });
        }

        if (treatmentStats.dueToday.length > 0) {
                alerts.push({
                        type: "treatments",
                        severity: "medium",
                        message: `${treatmentStats.dueToday.length} treatments scheduled for today`,
                        relatedTreatments: treatmentStats.dueToday.slice(0, 10),
                });
        }

        const urgentPredictions = predictionStats.health.length + predictionStats.birth.length + predictionStats.disease.length;
        if (urgentPredictions > 0) {
                alerts.push({
                        type: "predictions",
                        severity: urgentPredictions > 3 ? "high" : "medium",
                        message: `${urgentPredictions} critical predictions (health/birth/disease)`,
                        relatedPredictions: [
                                ...predictionStats.health.slice(0, 5),
                                ...predictionStats.birth.slice(0, 5),
                                ...predictionStats.disease.slice(0, 5),
                        ],
                });
        }

        return alerts;
}

export function summarizeGroups(groupEntries) {
        if (!groupEntries || groupEntries.length === 0) {
                return "No group assignments available.";
        }

        const parts = groupEntries.map(({ name, count }) => `${name}: ${count}`);
        return parts.join(", ");
}

export function buildContextFragments({ animalStats, treatmentStats, predictionStats, productionStats, alerts }) {
        const fragments = [];

        fragments.push(`Herd size: ${animalStats.total || 0}`);
        if (typeof animalStats.averageHealthScore === "number") {
                fragments.push(`Average health score: ${animalStats.averageHealthScore}`);
        }
        fragments.push(`Suspicious animals: ${animalStats.suspiciousCount}`);

        const groupSummary = summarizeGroups(animalStats.groups);
        fragments.push(`Group distribution: ${groupSummary}`);

        fragments.push(`Active treatments: ${treatmentStats.active.length}`);
        fragments.push(`Overdue treatments: ${treatmentStats.overdue.length}`);
        fragments.push(`Due today: ${treatmentStats.dueToday.length}`);

        fragments.push(`Predictions - Disease: ${predictionStats.disease.length}, Birth: ${predictionStats.birth.length}, Health: ${predictionStats.health.length}`);

        if (productionStats) {
                fragments.push(
                        `Production - Calvings: ${productionStats.totalBirths || 0}, Recent (30d): ${productionStats.recentBirths || 0}`
                );
                if (typeof productionStats.birthMetrics?.averageLiving === "number") {
                        fragments.push(`Avg live born: ${productionStats.birthMetrics.averageLiving}`);
                }
        }

        if (alerts.length > 0) {
                const alertText = alerts.map((alert) => `${alert.message} [${alert.severity}]`).join("; ");
                fragments.push(`Priority alerts: ${alertText}`);
        }

        return fragments;
}

export function analyzeDevices(nodes = []) {
        if (!nodes || nodes.length === 0) {
                return {
                        total: 0,
                        connected: 0,
                        disconnected: 0,
                        healthy: 0,
                        warning: 0,
                        offline: 0,
                };
        }

        let connected = 0;
        let disconnected = 0;
        let healthy = 0;
        let warning = 0;
        let offline = 0;

        const now = Date.now();
        const FIVE_MINUTES = 5 * 60 * 1000;
        const FIFTEEN_MINUTES = 15 * 60 * 1000;

        nodes.forEach((node) => {
                const lastUpdate = node?.t || node?.timestamp || 0;
                const timeDiff = now - lastUpdate;

                if (timeDiff < FIVE_MINUTES) {
                        healthy++;
                        connected++;
                } else if (timeDiff < FIFTEEN_MINUTES) {
                        warning++;
                        connected++;
                } else {
                        offline++;
                        disconnected++;
                }
        });

        return {
                total: nodes.length,
                connected,
                disconnected,
                healthy,
                warning,
                offline,
        };
}

export function analyzeSensors(sensors = []) {
        if (!sensors || sensors.length === 0) {
                return {
                        total: 0,
                        active: 0,
                        byType: {},
                };
        }

        let active = 0;
        const byType = {};

        sensors.forEach((sensor) => {
                const type = sensor?.type || sensor?.Type || "unknown";
                byType[type] = (byType[type] || 0) + 1;

                const isActive = sensor?.isActive !== false && sensor?.active !== false && sensor?.Active !== false;
                if (isActive) {
                        active++;
                }
        });

        return {
                total: sensors.length,
                active,
                byType,
        };
}

function normalizeNumber(value) {
        if (typeof value === "number" && Number.isFinite(value)) {
                return value;
        }

        if (typeof value === "string" && value.trim().length > 0) {
                const parsed = Number(value.replace(/,/g, "."));
                return Number.isFinite(parsed) ? parsed : null;
        }

        return null;
}

export function analyzeProduction(births = [], weaningRecords = []) {
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        let recentBirths = 0;
        let recentWeanings = 0;

        let totalLiving = 0;
        let totalDead = 0;
        let totalWeaned = 0;
        let assistedCount = 0;
        let birthRecordsWithLiving = 0;
        let birthRecordsWithDead = 0;
        let birthRecordsWithWeaned = 0;

        const birthPerAnimal = new Map();
        const weaningPerAnimal = new Map();

        const latestBirths = [];
        const latestWeanings = [];

        births.forEach((birth) => {
                const birthDate =
                        safeDate(birth?.StartDate || birth?.Date || birth?.date || birth?.t) || safeDate(birth?.StartTime);
                const living = normalizeNumber(birth?.Living);
                const dead = normalizeNumber(birth?.Dead);
                const weaned = normalizeNumber(birth?.Weaned);
                const assisted = typeof birth?.Assisted === "boolean" ? birth.Assisted : null;

                if (birthDate) {
                        latestBirths.push({ data: birth, date: birthDate });
                        if (birthDate >= thirtyDaysAgo) {
                                recentBirths++;
                        }
                }

                if (typeof living === "number") {
                        totalLiving += living;
                        birthRecordsWithLiving++;
                }

                if (typeof dead === "number") {
                        totalDead += dead;
                        birthRecordsWithDead++;
                }

                if (typeof weaned === "number") {
                        totalWeaned += weaned;
                        birthRecordsWithWeaned++;
                }

                if (assisted === true) {
                        assistedCount++;
                }

                const animalId = birth?.ID || birth?.id || birth?.AnimalId || birth?.animalId;
                if (animalId) {
                        const current = birthPerAnimal.get(animalId) || {
                                id: animalId,
                                births: 0,
                                living: 0,
                                dead: 0,
                                lastEvent: null,
                        };

                        current.births += 1;
                        current.living += typeof living === "number" ? living : 0;
                        current.dead += typeof dead === "number" ? dead : 0;
                        if (!current.lastEvent || (birthDate && birthDate > current.lastEvent)) {
                                current.lastEvent = birthDate || current.lastEvent;
                        }

                        birthPerAnimal.set(animalId, current);
                }
        });

        weaningRecords.forEach((weaning) => {
                const weanDate = safeDate(
                        weaning?.StartDate || weaning?.Date || weaning?.date || weaning?.t
                );

                if (weanDate) {
                        latestWeanings.push({ data: weaning, date: weanDate });
                        if (weanDate >= thirtyDaysAgo) {
                                recentWeanings++;
                        }
                }

                const animalId = weaning?.ID || weaning?.id || weaning?.AnimalId || weaning?.animalId;
                if (animalId) {
                        const current = weaningPerAnimal.get(animalId) || {
                                id: animalId,
                                weanings: 0,
                                lastEvent: null,
                        };

                        current.weanings += 1;
                        if (!current.lastEvent || (weanDate && weanDate > current.lastEvent)) {
                                current.lastEvent = weanDate || current.lastEvent;
                        }

                        weaningPerAnimal.set(animalId, current);
                }
        });

        latestBirths.sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));
        latestWeanings.sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));

        const topBirthAnimals = Array.from(birthPerAnimal.values())
                .sort((a, b) => b.births - a.births || (b.lastEvent?.getTime?.() || 0) - (a.lastEvent?.getTime?.() || 0))
                .slice(0, 5);

        const topWeaningAnimals = Array.from(weaningPerAnimal.values())
                .sort((a, b) => b.weanings - a.weanings || (b.lastEvent?.getTime?.() || 0) - (a.lastEvent?.getTime?.() || 0))
                .slice(0, 5);

        const birthMetrics = {
                averageLiving: birthRecordsWithLiving > 0 ? Math.round((totalLiving / birthRecordsWithLiving) * 10) / 10 : null,
                averageDead: birthRecordsWithDead > 0 ? Math.round((totalDead / birthRecordsWithDead) * 10) / 10 : null,
                averageWeaned:
                        birthRecordsWithWeaned > 0 ? Math.round((totalWeaned / birthRecordsWithWeaned) * 10) / 10 : null,
                assistedRate: births.length > 0 ? Math.round((assistedCount / births.length) * 100) / 100 : 0,
        };

        return {
                totalBirths: births.length,
                totalWeanings: weaningRecords.length,
                recentBirths,
                recentWeanings,
                birthMetrics,
                topBirthAnimals,
                topWeaningAnimals,
                latestBirths: latestBirths.slice(0, 5).map((entry) => entry.data),
                latestWeanings: latestWeanings.slice(0, 5).map((entry) => entry.data),
        };
}

export function analyzeWorkPlanner(workItems = []) {
        if (!workItems || workItems.length === 0) {
                return {
                        total: 0,
                        pending: 0,
                        inProgress: 0,
                        completed: 0,
                        cancelled: 0,
                        dueToday: 0,
                        overdue: 0,
                };
        }

        let pending = 0;
        let inProgress = 0;
        let completed = 0;
        let cancelled = 0;
        let dueToday = 0;
        let overdue = 0;

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        workItems.forEach((item) => {
                const status = item?.status || item?.Status || "pending";
                
                switch (status.toLowerCase()) {
                        case "pending":
                                pending++;
                                break;
                        case "in-progress":
                        case "inprogress":
                                inProgress++;
                                break;
                        case "completed":
                        case "done":
                                completed++;
                                break;
                        case "cancelled":
                        case "canceled":
                                cancelled++;
                                break;
                        default:
                                // Unknown status, ignore
                                break;
                }

                const startTime = item?.t || item?.startTime || item?.start || null;
                if (startTime && (status === "pending" || status === "in-progress" || status === "inprogress")) {
                        const startDate = safeDate(startTime);
                        if (startDate) {
                                const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                                if (startDay.getTime() === today.getTime()) {
                                        dueToday++;
                                } else if (startDay < today) {
                                        overdue++;
                                }
                        }
                }
        });

        return {
                total: workItems.length,
                pending,
                inProgress,
                completed,
                cancelled,
                dueToday,
                overdue,
        };
}
